import { DownOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { DeletedFilled } from 'assets/images/svg';
import { rolesConfig } from 'config/rolesConfig';
import { formatDisplayName } from 'library/helpers/utility';
import { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectRoles } from 'reduxStore/roles/selector';
import { roleActions } from 'reduxStore/roles/slice';
import { selectPeople } from 'reduxStore/people/selectors';
import { selectSites } from 'reduxStore/sites/selectors';
import { selectRoleList } from 'reduxStore/types/selectors';
import { Avatar, Button, Divider, Empty, Flex, Form, List, Modal, Select, Typography, Tag } from 'zui/Antd';

const { Title, Text } = Typography;

const ROLE_MAPPINGS = {
	globalAdmin: { permissionID: rolesConfig.GLOBAL_ADMIN, isSiteScoped: false },
	siteAdmin: { permissionID: rolesConfig.SITE_ADMIN, isSiteScoped: true },
	globalReadOnly: { permissionID: rolesConfig.GLOBAL_READ_ONLY, isSiteScoped: false },
	siteReadOnly: { permissionID: rolesConfig.SITE_READ_ONLY, isSiteScoped: true }
};

const AssignRoleModal = ({ visible, setVisible, roleType }) => {
	const {
		token: {
			fontSizeSM,
			marginLG,
			paddingXL,
			paddingLG,
			paddingXS,
			colorTextLabel,
			fontWeightStrong,
			marginXS,
			paddingSM,
			borderMD = 6,
			colorBorder,
			colorText,
			colorTextDescription,
			colorBorderSecondary
		}
	} = theme.useToken();

	const dispatch = useDispatch();
	const [form] = Form.useForm();

	// ✅ Selectors — filtered by site for site-scoped admins
	const people = useSelector(selectPeople);
	const roles = useSelector(selectRoles);
	const sites = useSelector(selectSites);
	const roleList = useSelector(selectRoleList);
	const { loadingAssignRole } = useSelector((state) => state.Roles);

	const [selectedPeople, setSelectedPeople] = useState([]);

	// ✅ Construcción dinámica del config leyendo de la DB
	const config = useMemo(() => {
		const mapping = ROLE_MAPPINGS[roleType] || ROLE_MAPPINGS.globalAdmin;

		const dbRole = roleList?.find((r) => r.PermissionID === mapping.permissionID);

		return {
			title: dbRole?.Name ? `Assign ${dbRole.Name}` : 'Assign Role',
			description: dbRole?.Descriptions || '',
			permissionID: mapping.permissionID,
			isSiteScoped: mapping.isSiteScoped
		};
	}, [roleType, roleList]);

	const handleCancel = () => {
		form.resetFields();
		setSelectedPeople([]);
		setVisible(false);
	};

	useEffect(() => {
		if (!visible) {
			form.resetFields();
			setSelectedPeople([]);
		}
	}, [visible, form]);

	const existingRolePersonIDs = useMemo(() => {
		if (config.isSiteScoped) return [];
		return roles.filter((role) => role.permissionID === config.permissionID).map((role) => role.personID);
	}, [roles, config]);

	// ✅ Averiguamos qué sitios ya tienen asignados las personas seleccionadas para ESTE rol en específico
	const assignedSiteIDs = useMemo(() => {
		if (!config.isSiteScoped || selectedPeople.length === 0) return new Set();

		const selectedIDs = selectedPeople.map((p) => p.personID);
		const relevantRoles = roles.filter(
			(r) => r.permissionID === config.permissionID && selectedIDs.includes(r.personID)
		);

		return new Set(relevantRoles.map((r) => r.entityID));
	}, [roles, config.permissionID, config.isSiteScoped, selectedPeople]);

	// ✅ Fix: label siempre es string — evita el bug de virtual scroll de Ant Design
	// que duplica items al hacer scroll cuando label es un React element.
	// El diseño custom (tag "Already Assigned") va en optionRender, no en label.
	const siteOptions = useMemo(
		() =>
			sites.map((site) => ({
				value: site.siteID,
				label: site.name, // string simple — requerido para fix del scroll
				disabled: assignedSiteIDs.has(site.siteID),
				isAssigned: assignedSiteIDs.has(site.siteID) // dato extra para optionRender
			})),
		[sites, assignedSiteIDs]
	);

	const availablePeople = useMemo(() => {
		return people
			.filter(
				(p) =>
					!existingRolePersonIDs.includes(p.personID) &&
					!selectedPeople.some((sp) => sp.personID === p.personID)
			)
			.map((user) => ({
				value: user.personID,
				label: formatDisplayName(user)?.displayName || '-'
			}));
	}, [people, selectedPeople, existingRolePersonIDs]);

	const handlePersonSelect = (personID) => {
		const person = people.find((p) => p.personID === personID);
		if (person && !selectedPeople.some((p) => p.personID === personID)) {
			setSelectedPeople([...selectedPeople, person]);
		}
		form.setFieldsValue({ selectedPerson: undefined });
	};

	const handleRemovePerson = (personID) => setSelectedPeople(selectedPeople.filter((p) => p.personID !== personID));
	const handleClearList = () => setSelectedPeople([]);

	const handleAssignRole = (values) => {
		if (selectedPeople.length === 0) {
			form.setFields([{ name: 'selectedPerson', errors: ['Please select at least one person.'] }]);
			return;
		}

		const { site } = values;

		selectedPeople.forEach((person) => {
			dispatch(
				roleActions.assignRole({
					personID: person.personID,
					permissionID: config.permissionID,
					entityID: config.isSiteScoped ? site : person.orgID
				})
			);
		});

		handleCancel();
	};

	return (
		<Modal
			open={visible}
			onCancel={handleCancel}
			closeIcon={false}
			centered
			destroyOnClose
			width={634}
			footer={[
				<div key='footer-container'>
					<Divider
						style={{
							color: colorBorderSecondary,
							margin: `${paddingXL}px -${paddingXL}px`,
							width: `calc(100% + ${paddingXL * 2}px)`
						}}
					/>
					<Flex align='center' justify='flex-end' style={{ marginTop: marginLG }} gap={paddingSM}>
						<Button onClick={handleCancel} disabled={loadingAssignRole} type='default'>
							Cancel
						</Button>
						<Button type='primary' form='assign-role-form' htmlType='submit' loading={loadingAssignRole}>
							Assign Role
						</Button>
					</Flex>
				</div>
			]}
			styles={{ content: { padding: paddingXL } }}
		>
			<Form
				id='assign-role-form'
				form={form}
				layout='vertical'
				style={{ display: 'flex', flexDirection: 'column' }}
				onFinish={handleAssignRole}
			>
				<Flex vertical style={{ marginBottom: paddingLG }}>
					<Title level={4} style={{ margin: 0 }}>
						{config.title}
					</Title>
					{config.description && (
						<Text style={{ fontSize: fontSizeSM, color: colorTextLabel, lineHeight: '20px' }}>
							{config.description}
						</Text>
					)}
				</Flex>

				<Flex vertical gap={paddingLG} style={{ marginBottom: paddingXL }}>
					<Form.Item
						name='selectedPerson'
						label='Name'
						style={{ fontWeight: fontWeightStrong, marginBottom: 0 }}
						required={false}
					>
						<Select
							placeholder='Select a Person'
							options={availablePeople}
							showSearch
							optionLabelProp='label'
							onSelect={handlePersonSelect}
							filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
							suffixIcon={<DownOutlined style={{ fontSize: fontSizeSM }} />}
							loading={loadingAssignRole}
							style={{ fontWeight: 400 }}
						/>
					</Form.Item>

					{/* Site Selector */}
					{config.isSiteScoped && (
						<Form.Item
							label='Site'
							name='site'
							rules={[{ required: true, message: 'Please select a site.' }]}
							style={{ fontWeight: fontWeightStrong, marginBottom: 0 }}
							required={false}
						>
							<Select
								placeholder='Select a Site'
								options={siteOptions}
								showSearch
								filterOption={(input, option) =>
									option?.label?.toLowerCase().includes(input.toLowerCase())
								}
								style={{ fontWeight: 400 }}
								allowClear
								virtual={false}
								optionRender={(option) => (
									<Flex justify='space-between' align='center'>
										<Text
											style={{ color: option.data.isAssigned ? colorTextDescription : 'inherit' }}
										>
											{option.label}
										</Text>
										{option.data.isAssigned && (
											<Tag bordered={false} style={{ margin: 0, color: colorTextDescription }}>
												Already Assigned
											</Tag>
										)}
									</Flex>
								)}
							/>
						</Form.Item>
					)}
				</Flex>

				<Flex vertical>
					<Flex align='center' justify='space-between' style={{ marginBottom: marginXS }}>
						<Text strong>Selected People</Text>
						{selectedPeople.length > 0 && (
							<Button type='text' onClick={handleClearList} style={{ textDecoration: 'underline' }}>
								Clear List
							</Button>
						)}
					</Flex>

					<Flex
						style={{
							height: 145,
							overflowY: 'auto',
							padding: `${paddingSM}px 0px`,
							borderRadius: borderMD,
							border: `1px solid ${colorBorder}`,
							width: 'calc(100% - 2px)'
						}}
					>
						{selectedPeople.length > 0 ? (
							<List
								style={{ width: '100%', padding: `0px ${paddingXS}px` }}
								dataSource={selectedPeople}
								renderItem={(person) => (
									<List.Item
										style={{ padding: `0px ${paddingSM}px`, border: 0, marginBottom: paddingXS }}
										extra={
											<Button
												icon={<DeletedFilled width={16} height={16} />}
												onClick={() => handleRemovePerson(person.personID)}
												type='text'
												disabled={loadingAssignRole}
											/>
										}
									>
										<List.Item.Meta
											avatar={
												<Avatar
													size={30}
													style={{
														fontSize: fontSizeSM,
														color: colorText,
														fontWeight: fontWeightStrong,
														backgroundColor: '#D9D9D9'
													}}
												>
													{formatDisplayName(person)?.displayName?.charAt(0).toUpperCase()}
												</Avatar>
											}
											title={
												<Text style={{ fontWeight: 400 }}>
													{formatDisplayName(person)?.displayName}
												</Text>
											}
										/>
									</List.Item>
								)}
							/>
						) : (
							<Flex align='center' justify='center' style={{ height: '100%', width: '100%' }}>
								<Empty description={'No people selected yet.'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
							</Flex>
						)}
					</Flex>
				</Flex>
			</Form>
		</Modal>
	);
};

export default AssignRoleModal;
